import React, { useEffect, useMemo, useState, useContext } from 'react';
import { Alert, Linking, Platform, Pressable, Image, Dimensions } from 'react-native'
import { useQuery } from '@tanstack/react-query';
import { Text, Title } from '@constants/Themed';
import apis from '@api/api';
import DropDownView from '@components/Global/DropDownView';
import styles from '@stylesheet';
import moment from 'moment';
import getCurrency from '@hooks/getCurrency';
import HandleIcon from '@hooks/handlePlanIcon';
import Button from '@components/UI/Button/Button';
import AnimatedLottieView from '@utils/lottieAnimation';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserBilling } from '@reduxLocal/selectors/userSelectors';
import { DefaultRootState } from '@reduxLocal/persistState';
import { SizableText, XStack, View, H5, H6, Separator, H2, H1, YStack, } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useTranslation } from 'react-i18next';
import toTitleCase from '@hooks/toTitleCase';
import MaskedView from '@react-native-masked-view/masked-view';
import { LinearGradient } from 'expo-linear-gradient';
import * as Animatable from 'react-native-animatable';
import { BillingInfo } from '../types/types';
import handleCancelPlan, { PlanType } from '../utils/handleCancelPlan';
import { AlertDialogContext } from '@components/Global/AlertDialog/AlertDialogProvider';
import handlePlanRenewal from '../utils/handlePlanRenewal';
import LottieView from 'lottie-react-native';
import { HIDE_PAYMENT_REQUIRED } from '@reduxLocal/actions/types';
import RowComponent from '@components/Global/RowComponent/RowComponent';
import { AntDesign, MaterialIcons, Feather } from '@expo/vector-icons';

type StripeLikeObject = Record<string, any> | null | undefined;

type IntervalDetails = {
  interval: string;
  intervalCount: number;
};

const coerceCurrency = (code?: string, fallbackSymbol?: string) => {
  if (!code && fallbackSymbol) {
    return fallbackSymbol;
  }

  if (!code || typeof code !== 'string') {
    return fallbackSymbol || getCurrency('gbp');
  }

  const normalizedCode = code.toLowerCase();

  try {
    return getCurrency(normalizedCode);
  } catch (error) {
    return fallbackSymbol || getCurrency('gbp');
  }
};

const extractCurrencySymbol = (...candidates: StripeLikeObject[]): string | undefined => {
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object') continue;

    const metadataSymbol = candidate?.metadata?.currency_symbol || candidate?.metadata?.currencySymbol;
    if (metadataSymbol) {
      return metadataSymbol;
    }

    if (candidate?.currencySymbol) {
      return candidate.currencySymbol;
    }
  }

  return undefined;
};

const extractCurrencyCode = (...candidates: StripeLikeObject[]): string | undefined => {
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object') continue;

    if (candidate?.currency) {
      return candidate.currency;
    }

    if (candidate?.default_price?.currency) {
      return candidate.default_price.currency;
    }

    if (candidate?.plan?.currency) {
      return candidate.plan.currency;
    }

    if (candidate?.items?.data?.[0]?.plan?.currency) {
      return candidate.items.data[0].plan.currency;
    }

    const metadataCurrency = candidate?.metadata?.currency || candidate?.metadata?.default_currency;
    if (metadataCurrency) {
      return metadataCurrency;
    }
  }

  return undefined;
};

const resolveIntervalDetails = (planLike: StripeLikeObject): IntervalDetails | null => {
  if (!planLike || typeof planLike !== 'object') {
    return null;
  }

  const metadata = planLike.metadata || planLike.product?.metadata;
  const recurring = planLike.recurring || planLike.default_price?.recurring || planLike.plan?.recurring;

  const interval = recurring?.interval
    || planLike.interval
    || planLike.billing_interval
    || metadata?.billing_interval
    || metadata?.interval;

  const intervalCountRaw = recurring?.interval_count
    || planLike.interval_count
    || metadata?.interval_count;

  if (interval) {
    const intervalCount = intervalCountRaw ? Number(intervalCountRaw) : 1;
    const safeIntervalCount = Number.isFinite(intervalCount) && intervalCount > 0 ? intervalCount : 1;
    return {
      interval: String(interval).toLowerCase(),
      intervalCount: safeIntervalCount,
    };
  }

  if (planLike.plan) {
    const derived = resolveIntervalDetails(planLike.plan);
    if (derived) {
      return derived;
    }
  }

  if (Array.isArray(planLike.items?.data)) {
    for (const item of planLike.items.data) {
      const nested = resolveIntervalDetails(item);
      if (nested) {
        return nested;
      }
    }
  }

  const defaultInterval = metadata?.default_interval;
  const defaultIntervalCount = metadata?.default_interval_count;

  if (defaultInterval) {
    const safeCount = defaultIntervalCount ? Number(defaultIntervalCount) : 1;
    return {
      interval: String(defaultInterval).toLowerCase(),
      intervalCount: Number.isFinite(safeCount) && safeCount > 0 ? safeCount : 1,
    };
  }

  return null;
};

const deriveSubscriptionInterval = (currentPlan: StripeLikeObject): IntervalDetails => {
  if (!currentPlan) {
    return { interval: 'month', intervalCount: 1 };
  }

  const sources: StripeLikeObject[] = [
    currentPlan,
    currentPlan?.plan,
    currentPlan?.product,
    currentPlan?.product?.default_price,
    currentPlan?.default_price,
  ];

  const nestedData = currentPlan?.data?.data ?? currentPlan?.data;
  if (Array.isArray(nestedData)) {
    nestedData.forEach((item) => {
      sources.push(item, item?.plan, item?.items?.data?.[0], item?.items?.data?.[0]?.plan);
    });
  } else if (nestedData && typeof nestedData === 'object') {
    sources.push(nestedData, nestedData.plan, nestedData?.items?.data?.[0], nestedData?.items?.data?.[0]?.plan);
  }

  for (const source of sources) {
    const details = resolveIntervalDetails(source);
    if (details?.interval) {
      return details;
    }
  }

  return { interval: 'month', intervalCount: 1 };
};

const formatIntervalLabel = (interval: string, intervalCount: number) => {
  if (!interval) {
    return '';
  }

  const normalizedInterval = interval.toLowerCase();
  const count = intervalCount && Number(intervalCount) > 0 ? Number(intervalCount) : 1;

  if (count === 1) {
    return normalizedInterval;
  }

  const pluralBase = normalizedInterval.endsWith('s') ? normalizedInterval : `${normalizedInterval}s`;
  return `${count} ${pluralBase}`;
};

const PlanComponent = ({ minimal, billingInfo, upgradeRef, planCallback, manageRef }) => {
  const [product, setProduct] = useState("");
  const [subState, setSubState] = useState("");
  const [windowWidth, setWindowWidth] = useState(Dimensions.get('window').width);
  const userBilling = useSelector(selectUserBilling);
  const customerId = userBilling?.billingId;
  const theme = themeSelector();
  const { t } = useTranslation();
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);

  // Use useQuery to fetch subscription plan data if not in cache
  const { data: currentPlan, isLoading: planIsLoading } = useQuery({
    queryKey: ['subscriptionPlan'],
    queryFn: async () => {
      const data = await apis.getSubscriptions();
      return data;
    },
    refetchOnMount: true,
  });

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setWindowWidth(window.width);
    });
    return () => subscription?.remove();
  }, []);

  const planNameFontSize = useMemo(() => {
    return windowWidth < 768 ? 16 : 20;
  }, [windowWidth]);

  const normalizedPlan = useMemo(() => {
    if (!currentPlan) {
      return null;
    }

    const currentPlanAny = currentPlan as any;

    const nestedData = currentPlanAny?.data?.data ?? currentPlanAny?.data;
    if (Array.isArray(nestedData) && nestedData[0]) {
      return nestedData[0];
    }

    return currentPlanAny;
  }, [currentPlan]);

  // Helper to check if user is in trial
  const isInTrial = useMemo(() => {
    if (!normalizedPlan) return false;
    // Check explicit trialing status
    if (normalizedPlan.status === 'trialing') return true;
    // Also check if trial_end exists and is in the future
    if (normalizedPlan.trial_end) {
      const trialEndDate = new Date(normalizedPlan.trial_end * 1000);
      const now = new Date();
      if (trialEndDate > now) {
        return true; // Still in trial period
      }
    }
    return false;
  }, [normalizedPlan]);

  const currencySymbol = useMemo(() => {
    const currentPlanAny = currentPlan as any;
    const candidateSymbol = extractCurrencySymbol(currentPlanAny, normalizedPlan, normalizedPlan?.plan, normalizedPlan?.items?.data?.[0], normalizedPlan?.product);
    const candidateCode = extractCurrencyCode(currentPlanAny, normalizedPlan, normalizedPlan?.plan, normalizedPlan?.items?.data?.[0], normalizedPlan?.product);

    return coerceCurrency(candidateCode || currentPlanAny?.currency, candidateSymbol);
  }, [currentPlan, normalizedPlan]);

  const subscriptionInterval = useMemo(() => {
    if (normalizedPlan?.plan?.interval) {
      const intervalCount = normalizedPlan?.plan?.interval_count || 1;
      return {
        interval: String(normalizedPlan.plan.interval).toLowerCase(),
        intervalCount: Number.isFinite(Number(intervalCount)) && Number(intervalCount) > 0 ? Number(intervalCount) : 1,
      };
    }

    return deriveSubscriptionInterval(normalizedPlan || currentPlan);
  }, [normalizedPlan, currentPlan]);

  const planIntervalLabel = useMemo(() => formatIntervalLabel(subscriptionInterval.interval, subscriptionInterval.intervalCount), [subscriptionInterval]);

  // Check if product is EXEMPT_PRODUCT
  const isExemptProduct = useMemo(() => {
    if (!currentPlan) return false;

    const productName =
      (currentPlan as any)?.product ||
      currentPlan.product?.name ||
      currentPlan.productName ||
      normalizedPlan?.plan?.product ||
      normalizedPlan?.product?.name;

    return productName === 'EXEMPT_PRODUCT';
  }, [currentPlan, normalizedPlan]);

  const formattedPrice = useMemo(() => {
    const priceSource = normalizedPlan?.plan?.amount
      ?? normalizedPlan?.plan?.amount_due
      ?? normalizedPlan?.default_price?.unit_amount
      ?? normalizedPlan?.amount
      ?? currentPlan?.price;

    if (priceSource === undefined || priceSource === null) {
      return null;
    }

    const rawAmount = Number(priceSource);
    if (!Number.isFinite(rawAmount)) {
      return null;
    }

    const amount = rawAmount >= 1 ? rawAmount : rawAmount * 100;

    return (amount / 100);
  }, [normalizedPlan, currentPlan?.price]);


  const handleCustomerPortal = async () => {
    await apis.getCustomerPortal({ accountId: customerId }).then((res) => {
      if (res.url) {
        if (Platform.OS === 'web') {
          window.open(res.url, '_blank');
        } else {
          Linking.openURL(res.url);
        }
      }
    })
  }

  const getBillingIcon = (type: string) => {
    const iconProps = {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3
    };

    switch (type) {
      case 'stripe':
        return <Image source={require('@assets/images/logos/stripe.png')} style={{ ...iconProps, width: 80, height: 40, resizeMode: 'contain' }} />;
      case 'appStore':
        return <Image source={require('@assets/images/Apple-logo.png')} style={{ ...iconProps, width: 40, height: 40, resizeMode: 'contain' }} />;
      case 'googlePlay':
        return <Image source={require("@assets/images/logos/google.png")} style={{ ...iconProps, width: 80, height: 40, resizeMode: 'contain' }} />;
      default:
        return <MaterialIcons name="payment" size={32} color={theme ? "#6366f1" : "#818cf8"} />;
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return { bg: theme ? '#dcfce7' : '#064e3b', text: theme ? '#16a34a' : '#22c55e' };
      case 'trialing':
        return { bg: theme ? '#fef3c7' : '#451a03', text: theme ? '#d97706' : '#fbbf24' };
      case 'expired':
        return { bg: theme ? '#fecaca' : '#7f1d1d', text: theme ? '#dc2626' : '#ef4444' };
      default:
        return { bg: theme ? '#f1f5f9' : '#334155', text: theme ? '#64748b' : '#94a3b8' };
    }
  };

  const ActionButton = ({ onPress, icon, label, variant = 'primary', disabled = false }) => {
    const getVariantStyles = () => {
      switch (variant) {
        case 'danger':
          return {
            bg: disabled ? '#9ca3af' : '#ef4444',
            bgPressed: '#dc2626',
            shadow: '#ef4444'
          };
        case 'secondary':
          return {
            bg: disabled ? '#9ca3af' : (theme ? '#6366f1' : '#818cf8'),
            bgPressed: theme ? '#4f46e5' : '#6366f1',
            shadow: theme ? '#6366f1' : '#818cf8'
          };
        default:
          return {
            bg: disabled ? '#9ca3af' : (theme ? '#059669' : '#10b981'),
            bgPressed: theme ? '#047857' : '#065f46',
            shadow: '#059669'
          };
      }
    };

    const variantStyles = getVariantStyles();

    return (
      <Pressable
        onPress={onPress}
        disabled={disabled}
        style={({ pressed }) => ({
          backgroundColor: pressed ? variantStyles.bgPressed : variantStyles.bg,
          borderRadius: 12,
          paddingVertical: 16,
          paddingHorizontal: 24,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          shadowColor: variantStyles.shadow,
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: disabled ? 0 : 0.2,
          shadowRadius: 8,
          elevation: disabled ? 0 : 6,
          transform: [{ scale: pressed ? 0.98 : 1 }],
          flex: 1,
          minWidth: 120,
          opacity: disabled ? 0.6 : 1
        })}
      >
        {icon && <AntDesign name={icon} size={18} color="white" />}
        <Text style={{ 
          color: 'white', 
          fontWeight: '600', 
          fontSize: 15 
        }}>
          {label}
        </Text>
      </Pressable>
    );
  };

  if (!currentPlan && planIsLoading) {
    return (
      <View style={{ 
        padding: 24, 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: 200
      }}>
        <LottieView
          source={require('@assets/animations/spin-leaf-loader.json')}
          autoPlay
          loop
          style={{ width: 80, height: 80 }} 
        />
        <Text style={{ 
          marginTop: 16, 
          color: theme ? '#64748b' : '#94a3b8',
          fontSize: 16 
        }}>
          Loading subscription details...
        </Text>
      </View>
    );
  }

  if (!currentPlan) {
    return (
      <View style={{ 
        padding: 24, 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: 200,
        gap: 20
      }}>
        <View style={{
          backgroundColor: theme ? '#f8fafc' : '#1e293b',
          borderRadius: 16,
          padding: 20,
          alignItems: 'center',
          borderWidth: 2,
          maxHeight: 200,
          borderColor: theme ? '#e2e8f0' : '#334155',
          borderStyle: 'dashed'
        }}>
          {HandleIcon(4)}
        </View>
        <View style={{ alignItems: 'center', gap: 8 }}>
          <H6 style={{ 
            color: theme ? '#1e293b' : '#f1f5f9',
            fontWeight: '700'
          }}>
            {subState || 'No Active Plan'}
          </H6>
          <Text style={{ 
            color: theme ? '#64748b' : '#94a3b8',
            textAlign: 'center',
            fontSize: 14
          }}>
            You don't have an active subscription
          </Text>
        </View>
      </View>
    );
  }

  // For EXEMPT_PRODUCT, treat expired status as active
  const displayStatus = isExemptProduct && currentPlan.status === 'expired' ? 'active' : currentPlan.status;
  const statusColors = getStatusColor(displayStatus);

  return (
    <View style={{ minHeight: 400, gap: 24, flex: 1 }}>
      {/* Plan Overview Card */}
      <View style={{
        backgroundColor: theme ? '#ffffff' : '#1e293b',
        borderRadius: 16,
        flex: 1,
        alignSelf: 'stretch',
        padding: 24,
        borderWidth: 1,
        borderColor: theme ? '#e2e8f0' : '#334155',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: theme ? 0.05 : 0.15,
        shadowRadius: 8,
        elevation: 3,
      }}>
        <YStack flex={1} flexWrap='wrap' gap={20} alignItems="center">
          {/* Provider Icon */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#334155',
            borderRadius: 12,
            padding: 16,
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: Platform.select({ native: "100%" }),
            flexGrow: 1,
            alignSelf: 'stretch',
            minHeight: 80,
            flexBasis: 200,
          }}>
            {getBillingIcon(currentPlan.type)}
          </View>

          {/* Plan Details */}
          <View style={{ flexGrow: 1, flexBasis: 200, flex: 1, gap: 12 }}>
            <XStack flexWrap='wrap' style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <H6 numberOfLines={1} style={{
                color: theme ? '#1e293b' : '#f1f5f9',
                fontWeight: '700',
                fontSize: planNameFontSize
              }}>
                {(currentPlan as any)?.product || currentPlan.product?.name || normalizedPlan?.plan?.product || 'Unknown Plan'}
              </H6>
              {currentPlan.status && !(isExemptProduct && currentPlan.status === 'expired') && (
                <XStack flexWrap='wrap' style={{
                  backgroundColor: statusColors.bg,
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 8,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6
                }}>
                  {currentPlan.status === 'expired' && (
                    <MaterialIcons name="warning" size={16} color={statusColors.text} />
                  )}
                  <Text style={{
                    color: statusColors.text,
                    fontSize: 12,
                    fontWeight: '600',
                    textTransform: 'capitalize'
                  }}>
                    {toTitleCase(currentPlan.status)}
                  </Text>
                </XStack>
              )}
              {isExemptProduct && (
                <XStack flexWrap='wrap' style={{
                  backgroundColor: theme ? '#dbeafe' : '#1e3a8a',
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 8,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6
                }}>
                  <MaterialIcons name="all-inclusive" size={16} color={theme ? '#2563eb' : '#60a5fa'} />
                  <Text style={{
                    color: theme ? '#2563eb' : '#60a5fa',
                    fontSize: 12,
                    fontWeight: '600'
                  }}>
                    Unlimited AI Generation
                  </Text>
                </XStack>
              )}
            </XStack>

            {/* Pricing */}
            {!isInTrial && formattedPrice !== null && (
              <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 4 }}>
                <Text style={{
                  color: theme ? '#059669' : '#10b981',
                  fontSize: 28,
                  fontWeight: '700'
                }}>
                  {currencySymbol}{formattedPrice.toFixed(2)}
                </Text>
                {planIntervalLabel ? (
                  <Text style={{
                    color: theme ? '#64748b' : '#94a3b8',
                    fontSize: 14,
                    fontWeight: '500'
                  }}>
                    /{planIntervalLabel}
                  </Text>
                ) : null}
              </View>
            )}

            {isInTrial && (
              <View style={{
                backgroundColor: theme ? '#fef3c7' : '#451a03',
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: 8,
                alignSelf: 'flex-start'
              }}>
                <Text style={{
                  color: theme ? '#d97706' : '#fbbf24',
                  fontSize: 14,
                  fontWeight: '600'
                }}>
                  🎉 Free Trial Active
                </Text>
              </View>
            )}
          </View>
        </YStack>
      </View>

      {/* Subscription Details - Show dates for both trial and active subscriptions */}
      {!minimal && (
        <View style={{
          backgroundColor: theme ? '#f8fafc' : '#0f172a',
          borderRadius: 12,
          padding: 20,
          gap: 12,
        }}>
          <H6 style={{
            color: theme ? '#374151' : '#d1d5db',
            fontWeight: '600',
            marginBottom: 8
          }}>
            {isInTrial ? 'Trial Information' : 'Subscription Details'}
          </H6>
          
          <View style={{ gap: 8 }}>
            {isInTrial ? (
              <>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text style={{ color: theme ? '#6b7280' : '#9ca3af', fontSize: 14 }}>
                    Trial Started
                  </Text>
                  <Text style={{ color: theme ? '#111827' : '#f3f4f6', fontSize: 14, fontWeight: '500' }}>
                    {normalizedPlan?.trial_start
                      ? moment.unix(normalizedPlan.trial_start).format(globalDate)
                      : normalizedPlan?.created
                        ? moment.unix(normalizedPlan.created).format(globalDate)
                        : currentPlan.startDate
                          ? moment(currentPlan.startDate).format(globalDate)
                          : 'N/A'}
                  </Text>
                </View>

                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text style={{
                    color: theme ? '#d97706' : '#fbbf24',
                    fontSize: 14,
                    fontWeight: '600'
                  }}>
                    Trial Ends
                  </Text>
                  <Text style={{
                    color: theme ? '#92400e' : '#fbbf24',
                    fontSize: 14,
                    fontWeight: '600'
                  }}>
                    {normalizedPlan?.trial_end
                      ? moment.unix(normalizedPlan.trial_end).format(globalDate)
                      : normalizedPlan?.current_period_end
                        ? moment.unix(normalizedPlan.current_period_end).format(globalDate)
                        : currentPlan?.endDate
                          ? moment(currentPlan.endDate).format(globalDate)
                          : 'N/A'}
                  </Text>
                </View>
              </>
            ) : (
              <>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ color: theme ? '#6b7280' : '#9ca3af', fontSize: 14 }}>
                      {t("subCreated")}
                    </Text>
                    <Text style={{ color: theme ? '#111827' : '#f3f4f6', fontSize: 14, fontWeight: '500' }}>
                      {currentPlan.startDate ? moment(currentPlan.startDate).format(globalDate) : 'N/A'}
                    </Text>
                  </View>

                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ color: theme ? '#6b7280' : '#9ca3af', fontSize: 14 }}>
                      {t("nextBillDue")}
                    </Text>
                    <Text style={{ color: theme ? '#111827' : '#f3f4f6', fontSize: 14, fontWeight: '500' }}>
                      {currentPlan?.endDate ? moment(currentPlan.endDate).format(globalDate) : 'N/A'}
                    </Text>
                  </View>
              </>
            )}
          </View>
        </View>
      )}

      {/* Trial Information Message with Add Payment Button */}
      {isInTrial && !minimal && (
        <View style={{
          backgroundColor: theme ? '#fefce8' : '#1a1408',
          borderRadius: 12,
          padding: 20,
          borderWidth: 1,
          borderColor: theme ? '#facc15' : '#a16207',
          gap: 16
        }}>
          <Text style={{
            color: theme ? '#a16207' : '#facc15',
            fontSize: 15,
            lineHeight: 22,
            textAlign: 'center'
          }}>
            🎯 Don't worry! To ensure you get the best out of SmartSaaS, you have been provided with free access to a paid plan. Add billing details before trial ends to continue your subscription.
          </Text>

          {/* Add Payment Method Button */}
          <Pressable
            onPress={handleCustomerPortal}
            style={({ pressed }) => ({
              backgroundColor: pressed ? (theme ? '#d97706' : '#ca8a04') : (theme ? '#f59e0b' : '#eab308'),
              borderRadius: 12,
              paddingVertical: 14,
              paddingHorizontal: 24,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              shadowColor: '#f59e0b',
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.3,
              shadowRadius: 8,
              elevation: 6,
              transform: [{ scale: pressed ? 0.98 : 1 }],
              marginTop: 8
            })}
          >
            <MaterialIcons name="credit-card" size={20} color="white" />
            <Text style={{
              color: 'white',
              fontSize: 16,
              fontWeight: '600'
            }}>
              Add Payment Method
            </Text>
            <MaterialIcons name="arrow-forward" size={18} color="white" />
          </Pressable>
        </View>
      )}

    </View>
  );
};

export default PlanComponent;